/**
 * Trade history table component (T156).
 *
 * Displays trade history with:
 * - Columns: Date, Symbol, Side, Quantity, Price, Value, P&L
 * - Pagination
 * - Color-coded P&L
 */
import { ChevronLeft, ChevronRight, TrendingDown, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import type { Trade } from '@/types/trading';

interface TradeHistoryTableProps {
  trades: Trade[];
  total: number;
  page: number;
  pageSize: number;
  totalPages?: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

export function TradeHistoryTable({
  trades,
  total,
  page,
  pageSize,
  totalPages = 1,
  onPageChange,
  isLoading = false,
}: TradeHistoryTableProps) {
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="p-4 text-left text-sm font-medium text-muted-foreground">Date</th>
                <th className="p-4 text-left text-sm font-medium text-muted-foreground">Symbol</th>
                <th className="p-4 text-left text-sm font-medium text-muted-foreground">Side</th>
                <th className="p-4 text-right text-sm font-medium text-muted-foreground">
                  Quantity
                </th>
                <th className="p-4 text-right text-sm font-medium text-muted-foreground">Price</th>
                <th className="p-4 text-right text-sm font-medium text-muted-foreground">Value</th>
                <th className="p-4 text-right text-sm font-medium text-muted-foreground">
                  Commission
                </th>
                <th className="p-4 text-center text-sm font-medium text-muted-foreground">
                  Source
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {[...Array(5)].map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="p-4">
                    <div className="h-4 bg-muted rounded w-24" />
                  </td>
                  <td className="p-4">
                    <div className="h-4 bg-muted rounded w-12" />
                  </td>
                  <td className="p-4">
                    <div className="h-4 bg-muted rounded w-14" />
                  </td>
                  <td className="p-4">
                    <div className="h-4 bg-muted rounded w-10 ml-auto" />
                  </td>
                  <td className="p-4">
                    <div className="h-4 bg-muted rounded w-16 ml-auto" />
                  </td>
                  <td className="p-4">
                    <div className="h-4 bg-muted rounded w-20 ml-auto" />
                  </td>
                  <td className="p-4">
                    <div className="h-4 bg-muted rounded w-12 ml-auto" />
                  </td>
                  <td className="p-4">
                    <div className="h-4 bg-muted rounded w-14 mx-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (trades.length === 0) {
    return (
      <div className="rounded-lg border bg-card p-8 text-center">
        <p className="text-muted-foreground">No trades found</p>
        <p className="text-sm text-muted-foreground mt-1">
          Trades will appear here once you start trading
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="p-4 text-left text-sm font-medium text-muted-foreground">Date</th>
              <th className="p-4 text-left text-sm font-medium text-muted-foreground">Symbol</th>
              <th className="p-4 text-left text-sm font-medium text-muted-foreground">Side</th>
              <th className="p-4 text-right text-sm font-medium text-muted-foreground">Quantity</th>
              <th className="p-4 text-right text-sm font-medium text-muted-foreground">Price</th>
              <th className="p-4 text-right text-sm font-medium text-muted-foreground">Value</th>
              <th className="p-4 text-right text-sm font-medium text-muted-foreground">
                Commission
              </th>
              <th className="p-4 text-center text-sm font-medium text-muted-foreground">Source</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {trades.map((trade) => (
              <tr key={trade.id} className="hover:bg-muted/50">
                <td className="p-4 text-sm">{formatDate(trade.executed_at || trade.created_at)}</td>
                <td className="p-4">
                  <span className="font-medium">{trade.symbol}</span>
                </td>
                <td className="p-4">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                      trade.side === 'buy'
                        ? 'bg-green-500/10 text-green-500'
                        : 'bg-red-500/10 text-red-500'
                    )}
                  >
                    {trade.side === 'buy' ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : (
                      <TrendingDown className="h-3 w-3" />
                    )}
                    {trade.side.toUpperCase()}
                  </span>
                </td>
                <td className="p-4 text-right tabular-nums">{trade.quantity}</td>
                <td className="p-4 text-right tabular-nums">{formatCurrency(trade.price)}</td>
                <td className="p-4 text-right font-medium tabular-nums">
                  {formatCurrency(trade.total_value)}
                </td>
                <td className="p-4 text-right tabular-nums text-muted-foreground">
                  {formatCurrency(trade.commission)}
                </td>
                <td className="p-4 text-center">
                  <span
                    className={cn(
                      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                      trade.source === 'agent'
                        ? 'bg-blue-500/10 text-blue-500'
                        : 'bg-gray-500/10 text-gray-500'
                    )}
                  >
                    {trade.source === 'agent' ? 'Agent' : 'Manual'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="border-t p-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {startItem}-{endItem} of {total} trades
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default TradeHistoryTable;
